"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.MAX_LENGTH = void 0;
exports.generateNodeLabel = generateNodeLabel;
exports.truncate = truncate;
/**
 * Maximum length of a generated label. `assignNodeLabels` also treats this as
 * the ceiling for a disambiguated label (base + numeric suffix), reserving the
 * suffix width within it.
 */
exports.MAX_LENGTH = 37;
/** Landmark/sectioning elements — high priority ancestors */
const LANDMARK_ANCESTORS = new Set([
    'footer',
    'header',
    'nav',
    'main',
    'aside',
    'section',
    'article',
    'form',
    'table',
    'dialog'
]);
/** List elements — lower priority, used only when no landmark is found */
const LIST_ANCESTORS = new Set(['ul', 'ol', 'dl']);
/**
 * Generates a short, deterministic, human-readable label for an
 * accessibility violation node.
 *
 * The label is ≤ `MAX_LENGTH` chars.
 *
 * Format: `{ancestor} {tag} · {label}` when a semantic ancestor is found,
 * otherwise falls back to `{tag}#{id} · {label}`, `{tag}.{class} · {label}`,
 * or simpler variants (down to `{tag}` alone).
 */
function generateNodeLabel(input) {
    const { html, xpath, ancestry, selector } = input;
    const tag = extractTag(html);
    if (!tag || !html) {
        return '';
    }
    const ancestor = extractAncestor(xpath, ancestry);
    const id = ancestor ? undefined : extractAttribute(html, 'id');
    const label = sanitizeLabel(extractLabel(html, !!id));
    const className = ancestor || id ? undefined : extractClass(html, selector);
    const prefix = buildPrefix(tag, ancestor, id, className);
    return compose(prefix, label);
}
function buildPrefix(tag, ancestor, id, className) {
    if (ancestor) {
        return `${ancestor} ${tag}`;
    }
    if (id) {
        return `${tag}#${id}`;
    }
    if (className) {
        return `${tag}.${className}`;
    }
    return tag;
}
function compose(prefix, label) {
    const separator = ' · ';
    const budget = exports.MAX_LENGTH - prefix.length - separator.length;
    // No label to append, or no room to append one — return the prefix alone.
    if (budget <= 0 || !label) {
        return truncate(prefix, exports.MAX_LENGTH);
    }
    return `${prefix}${separator}${truncate(label, budget)}`;
}
/**
 * Truncate `text` to at most `maxLength` characters, preferring a word boundary.
 *
 * - `text.length <= maxLength`: returned unchanged.
 * - Longer, with a space/hyphen at or before `maxLength`: cut at the nearest
 *   such boundary and `trimEnd`ed, so the trailing partial word is dropped
 *   (e.g. `"footer main nav"` → `"footer main"`).
 * - Longer, with no boundary in range (a single long token): hard-cut at
 *   `maxLength` (e.g. `"supercalifragilistic"` → first `maxLength` chars).
 */
function truncate(text, maxLength) {
    if (text.length <= maxLength) {
        return text;
    }
    // Walk back to the nearest word boundary
    let end = maxLength;
    while (end > 0 && text[end - 1] !== ' ' && text[end - 1] !== '-') {
        end--;
    }
    // If we walked all the way back, hard-cut at maxLength
    if (end === 0) {
        end = maxLength;
    }
    return text.slice(0, end).trimEnd();
}
function extractTag(html) {
    if (!html) {
        return undefined;
    }
    const match = html.match(/^<([a-zA-Z][a-zA-Z0-9]*)[\s>/]/);
    return match ? match[1].toLowerCase() : undefined;
}
function extractAncestor(xpath, ancestry) {
    const xpathResult = extractAncestorFromXpath(xpath);
    if (xpathResult) {
        return xpathResult;
    }
    return extractAncestorFromAncestry(ancestry);
}
function extractAncestorFromXpath(xpath) {
    if (!xpath || xpath.length === 0) {
        return undefined;
    }
    const xpathStr = xpath[xpath.length - 1];
    if (typeof xpathStr !== 'string') {
        return undefined;
    }
    const segments = xpathStr.split('/').filter(Boolean);
    const parentTags = segments
        .slice(0, -1)
        .map(s => s.replace(/\[.*\]$/, '').toLowerCase());
    return findBestAncestor(parentTags);
}
function extractAncestorFromAncestry(ancestry) {
    if (!ancestry || ancestry.length === 0) {
        return undefined;
    }
    const ancestryStr = ancestry[ancestry.length - 1];
    if (typeof ancestryStr !== 'string') {
        return undefined;
    }
    // Ancestry format: "html > body > footer > div > ul > li:nth-child(3) > a"
    const parts = ancestryStr.split('>').map(s => s.trim());
    const parentTags = parts.slice(0, -1).map(s => s
        .replace(/[.#:].*$/, '')
        .replace(/\[.*$/, '')
        .toLowerCase());
    return findBestAncestor(parentTags);
}
/**
 * Finds the best ancestor from a list of parent tag names (ordered root → element).
 * Prefers landmark elements over list elements. Within each tier, picks the
 * one closest to the element (highest index).
 */
function findBestAncestor(parentTags) {
    // First pass: look for landmark ancestors (innermost first)
    for (let i = parentTags.length - 1; i >= 0; i--) {
        if (LANDMARK_ANCESTORS.has(parentTags[i])) {
            return parentTags[i];
        }
    }
    // Second pass: fall back to list elements
    for (let i = parentTags.length - 1; i >= 0; i--) {
        if (LIST_ANCESTORS.has(parentTags[i])) {
            return parentTags[i];
        }
    }
    return undefined;
}
/**
 * Strips HTML metacharacters and control characters from an extracted label.
 * The label is a display aid derived verbatim from untrusted scanned DOM, so
 * guaranteeing it is plain text at the source means a downstream rendering
 * mistake cannot turn it into stored XSS. Only the free-text label portion
 * needs this; the prefix (tag/ancestor/id/class) is already metachar-free by
 * construction. The ` · ` separator is added after this runs, so U+00B7 is
 * unaffected.
 */
function sanitizeLabel(label) {
    if (!label) {
        return undefined;
    }
    const cleaned = label
        .replace(/[<>"']/g, '')
        // eslint-disable-next-line no-control-regex
        .replace(/[\x00-\x1F\x7F]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    return cleaned || undefined;
}
function extractLabel(html, skipRootId) {
    // 1. aria-label
    const ariaLabel = extractAttribute(html, 'aria-label');
    if (ariaLabel) {
        return ariaLabel.toLowerCase();
    }
    // 2. alt
    const alt = extractAttribute(html, 'alt');
    if (alt) {
        return alt.toLowerCase();
    }
    // 3. title
    const title = extractAttribute(html, 'title');
    if (title) {
        return title.toLowerCase();
    }
    // 4. Text content (strip child tags)
    const textContent = extractTextContent(html);
    if (textContent) {
        return textContent.toLowerCase();
    }
    // 5. href — extract meaningful part
    const href = extractAttribute(html, 'href');
    if (href) {
        const meaningful = extractMeaningfulHref(href);
        if (meaningful) {
            return meaningful.toLowerCase();
        }
    }
    // 6. value
    const value = extractAttribute(html, 'value');
    if (value) {
        return value.toLowerCase();
    }
    // 7. placeholder
    const placeholder = extractAttribute(html, 'placeholder');
    if (placeholder) {
        return placeholder.toLowerCase();
    }
    // 8. name
    const name = extractAttribute(html, 'name');
    if (name) {
        return name.toLowerCase();
    }
    // 9. type
    const type = extractAttribute(html, 'type');
    if (type) {
        return type.toLowerCase();
    }
    // 10. id or data-testid/data-test/data-cy on root element
    const rootDataLabel = extractDataLabel(html, false, skipRootId);
    if (rootDataLabel) {
        return rootDataLabel;
    }
    // 11. Child element aria-label (e.g. <li><a aria-label="Products">...</a></li>)
    const childAriaLabel = extractChildAttribute(html, 'aria-label');
    if (childAriaLabel) {
        return childAriaLabel.toLowerCase();
    }
    // 12. Child img alt text (e.g. <li><a><img alt="Product"></a></li>)
    const childAlt = extractChildAttribute(html, 'alt');
    if (childAlt) {
        return childAlt.toLowerCase();
    }
    // 13. Child element title
    const childTitle = extractChildAttribute(html, 'title');
    if (childTitle) {
        return childTitle.toLowerCase();
    }
    // 14. Child href — extract meaningful part
    const childHref = extractChildAttribute(html, 'href');
    if (childHref) {
        const meaningful = extractMeaningfulHref(childHref);
        if (meaningful) {
            return meaningful.toLowerCase();
        }
    }
    // 15. Child id or data-testid/data-test/data-cy
    const childDataLabel = extractDataLabel(html, true, false);
    if (childDataLabel) {
        return childDataLabel;
    }
    // 16. Child img src filename (e.g. <li><img src="/images/logo.png"></li>)
    const childImgSrc = extractChildImgSrc(html);
    if (childImgSrc) {
        return childImgSrc.toLowerCase();
    }
    // 17. SVG <use href="#icon_X">
    const svgUseMatch = html.match(/<use[^>]+href=["']#([^"']+)["']/);
    if (svgUseMatch) {
        return cleanIconName(svgUseMatch[1]).toLowerCase();
    }
    // 18. Inner SVG class name containing icon hint
    const svgClassMatch = html.match(/class=["'][^"']*\bsvg[-_]icon[-_]([a-zA-Z0-9_-]+)/);
    if (svgClassMatch) {
        return svgClassMatch[1].toLowerCase();
    }
    return undefined;
}
function extractAttribute(html, attr) {
    const openingTag = extractOpeningTag(html);
    if (!openingTag) {
        return undefined;
    }
    // Match attr="value" or attr='value' on the root element only.
    // Use (?<=\s) lookbehind to avoid matching "data-name" when looking for "name".
    const regex = new RegExp(`(?<=\\s)${attr}=["']([^"']*)["']`, 'i');
    const match = openingTag.match(regex);
    if (!match) {
        return undefined;
    }
    const value = match[1].trim();
    return value || undefined;
}
function extractTextContent(html) {
    // Get content between the opening tag and closing tag (or end of string)
    const openTagEnd = html.indexOf('>');
    if (openTagEnd === -1) {
        return undefined;
    }
    // Check for self-closing tags
    if (html[openTagEnd - 1] === '/') {
        return undefined;
    }
    const content = html.slice(openTagEnd + 1);
    // Strip all HTML tags
    const text = content.replace(/<[^>]*>/g, '').trim();
    // Collapse whitespace
    const collapsed = text.replace(/\s+/g, ' ').trim();
    return collapsed || undefined;
}
/**
 * Searches child elements (not the root tag) for an attribute value.
 * This handles cases like `<li><a><img alt="Product"></a></li>` where
 * the label-bearing attribute is nested multiple levels deep.
 */
function extractChildAttribute(html, attr) {
    const openTagEnd = html.indexOf('>');
    if (openTagEnd === -1) {
        return undefined;
    }
    // Skip the root element's opening tag
    const inner = html.slice(openTagEnd + 1);
    const regex = new RegExp(`(?<=\\s)${attr}=["']([^"']+)["']`, 'i');
    const match = inner.match(regex);
    return match ? match[1].trim() || undefined : undefined;
}
/**
 * Extracts a readable filename from a child <img src="..."> attribute.
 * E.g. `<li><img src="/images/blue-widget.png"></li>` → "blue-widget"
 */
function extractChildImgSrc(html) {
    const match = html.match(/<img[^>]+src=["']([^"']+)["']/i);
    if (!match) {
        return undefined;
    }
    const src = match[1];
    // Extract filename without extension
    const segments = src.split('/').filter(Boolean);
    const filename = segments.pop();
    if (!filename) {
        return undefined;
    }
    // Strip extension and query params
    const name = filename.replace(/\.[^.]+$/, '').replace(/[?#].*$/, '');
    // Skip generic/hash-like filenames (UUIDs, hashes, purely numeric)
    if (!name || /^[0-9a-f-]{16,}$/i.test(name) || /^\d+$/.test(name)) {
        return undefined;
    }
    return name;
}
/**
 * Known data-* attributes to check, ordered by descriptive value.
 * High: explicitly descriptive labels/names
 * Medium: testing/automation hooks (still human-readable)
 * Lower: generic identification
 */
const DATA_LABEL_ATTRS = [
    // Highest — standard HTML
    'id',
    // High — explicitly descriptive
    'data-label',
    'data-title',
    // Medium — testing conventions
    'data-testid',
    'data-test-id',
    'data-test',
    'data-cy',
    'data-qa',
    'data-e2e',
    'data-automation-id',
    // Lower — generic identification
    'data-name',
    'data-component',
    'data-section',
    'data-role',
    'data-id',
    'data-tracking',
    'data-type',
    'data-page',
    'data-action'
];
/** Set for fast lookup when falling back to generic data-* scan */
const DATA_LABEL_ATTRS_SET = new Set(DATA_LABEL_ATTRS);
/**
 * Extracts a human-readable label from id or common data-* attributes.
 * When `searchChildren` is true, searches child elements instead of the root.
 * Converts kebab-case, camelCase, and snake_case to space-separated words.
 * Falls back to any unknown data-* attribute if none of the known ones match.
 */
function extractDataLabel(html, searchChildren, skipId) {
    // First pass: check known attributes in priority order
    for (const attr of DATA_LABEL_ATTRS) {
        if (skipId && attr === 'id') {
            continue;
        }
        const value = searchChildren
            ? extractChildAttribute(html, attr)
            : extractAttribute(html, attr);
        if (value) {
            const cleaned = humanizeIdentifier(value);
            if (cleaned) {
                return cleaned.toLowerCase();
            }
        }
    }
    // Second pass: any data-* attribute not already checked
    return extractAnyDataAttribute(html, searchChildren);
}
/**
 * Scans for any data-* attribute not in the known list.
 * Returns the first humanizable value found.
 */
function extractAnyDataAttribute(html, searchChildren) {
    const source = searchChildren ? getInnerHtml(html) : extractOpeningTag(html);
    if (!source) {
        return undefined;
    }
    const regex = /(?<=\s)(data-[a-z][a-z0-9-]*)=["']([^"']+)["']/gi;
    let match;
    while ((match = regex.exec(source)) !== null) {
        const attr = match[1].toLowerCase();
        if (DATA_LABEL_ATTRS_SET.has(attr)) {
            continue;
        }
        const cleaned = humanizeIdentifier(match[2]);
        if (cleaned) {
            return cleaned.toLowerCase();
        }
    }
    return undefined;
}
function getInnerHtml(html) {
    const openTagEnd = html.indexOf('>');
    if (openTagEnd === -1) {
        return undefined;
    }
    return html.slice(openTagEnd + 1);
}
/**
 * Converts a programmatic identifier to a human-readable string. Splits on
 * camelCase boundaries and separators but does not change case (callers
 * lowercase the result).
 * E.g. "product-card-list" → "product card list"
 *      "addToCartBtn"      → "add To Cart Btn"
 *      "nav_main_item"     → "nav main item"
 */
function humanizeIdentifier(id) {
    const result = id
        // Insert space before camelCase boundaries (e.g. "addTo" → "add To")
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        // Replace hyphens, underscores, and dots with spaces
        .replace(/[-_.]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    return result || undefined;
}
function extractMeaningfulHref(href) {
    if (!href || href === '#' || href === '/') {
        return undefined;
    }
    // Try to parse as URL
    try {
        // Handle relative URLs
        const url = href.startsWith('http')
            ? new URL(href)
            : new URL(href, 'https://placeholder.com');
        // For absolute URLs, extract domain name (strip www. and TLD)
        if (href.startsWith('http')) {
            const hostname = url.hostname.replace(/^www\./, '');
            // Strip common TLDs for short domain names
            const domainParts = hostname.split('.');
            if (domainParts.length >= 2) {
                // Return the main domain name (e.g., "instagram" from "instagram.com")
                return domainParts[domainParts.length - 2];
            }
            return hostname;
        }
        // For paths, use the last non-empty segment
        const segments = url.pathname.split('/').filter(Boolean);
        if (segments.length > 0) {
            return segments[segments.length - 1];
        }
        return undefined;
    }
    catch {
        // Not a valid URL, extract last path-like segment
        return href.split('/').filter(Boolean).pop();
    }
}
function cleanIconName(name) {
    // Remove common prefixes like "icon_", "icon-", "ico-"
    return name.replace(/^icon[-_]?/i, '');
}
function extractClass(html, selector) {
    const classesFromHtml = extractClassesFromHtml(html);
    const classesFromSelector = extractClassesFromSelector(selector);
    const allClasses = [...classesFromHtml, ...classesFromSelector];
    if (allClasses.length === 0) {
        return undefined;
    }
    // Prefer BEM element classes (contain __)
    const bem = allClasses.find(cls => cls.includes('__'));
    if (bem) {
        return bem;
    }
    // Prefer long hyphenated classes (semantic names like hero-title, nav-item)
    const longHyphenated = allClasses.find(cls => cls.includes('-') && cls.length > 7);
    if (longHyphenated) {
        return longHyphenated;
    }
    // Fall back to any class (including utility classes)
    return allClasses[0];
}
function extractClassesFromHtml(html) {
    // Extract only the opening tag to avoid matching classes on child elements
    const openingTag = extractOpeningTag(html);
    if (!openingTag) {
        return [];
    }
    const match = openingTag.match(/class=["']([^"']*)["']/);
    if (!match) {
        return [];
    }
    return match[1].split(/\s+/).filter(Boolean);
}
function extractOpeningTag(html) {
    const end = html.indexOf('>');
    if (end === -1) {
        return undefined;
    }
    return html.slice(0, end + 1);
}
function extractClassesFromSelector(selector) {
    if (!selector || selector.length === 0) {
        return [];
    }
    const selectorStr = selector[selector.length - 1];
    if (typeof selectorStr !== 'string') {
        return [];
    }
    // Extract class names from CSS selector (e.g., ".foo.bar" -> ["foo", "bar"])
    const classes = [];
    const classRegex = /\.([a-zA-Z_][a-zA-Z0-9_-]*)/g;
    let match;
    while ((match = classRegex.exec(selectorStr)) !== null) {
        classes.push(match[1]);
    }
    return classes;
}
//# sourceMappingURL=generateNodeLabel.js.map